<script>
import { mapState, mapGetters } from 'vuex'
import imageUrlBuilder from '@sanity/image-url'
import cloneDeep from 'lodash/cloneDeep'

import client from '@sanityConfig'
import { pricingMixin } from '../../mixins/pricingMixin'
import Translate from '@components/translate'

const builder = imageUrlBuilder(client)

export default {
  data () {
    return {
      loading: false,
      error: ''
    }
  },
  mixins: [pricingMixin],
  computed: {
    ...mapGetters(['primaryColorRgb', 'activeSubscription', 'hasNativeSubscription', 'activeChallenge', 'hasActiveNativeSubscription', 'isAnnualWebOrNativeSubscriber', 'activeSubscriptionPlan', 'isChallengeFree']),
    ...mapState(['user', 'activeChallengePage', 'selectedOrder']),
    buttonText () {
      if (this.isChallengeFree) {
        return 'challenge.purchase.step1.unlockChallenge'
      } else {
        return 'challenge.purchase.step1.next'
      }
    },
    backgroundImage () {
      return this.activeChallengePage && this.activeChallengePage.challenge ? builder.image(this.activeChallengePage.challenge.challengeImage) : require('@assets/plankk.png')
    },
    challengeName () {
      return this.activeChallengePage && this.activeChallengePage.challenge ? this.activeChallengePage.challenge.challengeName : 'Challenge'
    },
    challengeDetails () {
      return this.activeChallengePage && this.activeChallengePage.challenge ? this.activeChallengePage.challenge.challengeDetail : ''
    }
  },
  components: {
    Translate
  },
  created () {
  },
  methods: {
    async handleClick () {
      if (this.isChallengeFree) {
        this.loading = true
        const userToken = this.$store.state.user.token
        const userId = this.$store.state.user._id

        try {
          window.Leanplum.track('Checkout', { action: 'attempted_checkout', user_id: userId, price_id: this.$store?.state?.selectedOrder?.plan, product_id: this.$store?.state?.selectedOrder?.sku || ' ' })
          this.$store.dispatch('startSession', { token: userToken, prices: [this.$store.state.selectedOrder.sku, this.$store.state.selectedOrder.plan] })
        } catch (error) {
          this.error = error.message ? {
            message: error.message,
            code: 'Something went wrong'
          } : {
            message: 'Something went wrong, Try again.',
            code: 'Something went wrong'
          }
          this.loading = false
        }
      } else {
        this.$emit('next')
      }
    },
    challengePrice (price) {
      if (price) {
        if (this.isAnnualWebOrNativeSubscriber && price.metadata.freeWithAnnual) {
          return '$0.00'
        } else {
          return this.$_priceFormat(price).split(' ')[0]
        }
      }
      return null
    },
    selectChallenge (price) {
      const selectedOrder = cloneDeep(this.selectedOrder)
      selectedOrder.sku = price.id
      this.$store.commit('setSelectedOrder', selectedOrder)
    }
  }
}
</script>

<template>
  <div class="container" :style="{ '--BGColor': `rgba(${primaryColorRgb}, ${1})`}">
    <div class="challenge-image">
      <div class="challenge-image-inside" :style="`background-image: url(${backgroundImage})`">
        <!-- <img :src="`${backgroundImage}`" alt="" width="100%"> -->
      </div>
    </div>
    <div class="challenge-details">
      <p class="challenge-header">{{challengeName}}</p>
      <p class="description">{{challengeDetails}}</p>
      <template v-for="(price,i) in activeChallenge.plans">
        <b-row
          class="challenge-card card mb-2"
          :class="[{ selected: price.id == selectedOrder.sku}]"
          :style="{ '--BGColor': `rgba(${primaryColorRgb}, ${1})` }"
          :key="`challenge-${i}`"
          @click="selectChallenge(price)"
        >
          <b-col cols="12" class="challenge-price">
              <div class="row">
                <div class="check-container">
                  <svg width="66" height="66" viewBox="0 0 66 66" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-check'" v-bind:svg-inline="''" v-if="price.id == selectedOrder.sku" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M33 66c18.225 0 33-14.775 33-33S51.225 0 33 0 0 14.775 0 33s14.775 33 33 33z" fill="url(#paint0_linear)" fill-opacity=".6"/><path d="M17.368 33.55l4.466-5.321 8.645 7.255 15.748-18.116 5.243 4.56L35.803 39.95l-4.466 5.322L17.368 33.55z" fill="#fff"/><defs><linearGradient id="paint0_linear" x1="33" y1="0" x2="33" y2="66" gradientUnits="userSpaceOnUse"><stop stop-color="#55BCF5"/><stop offset="1" stop-color="#3E4FE2"/></linearGradient></defs></svg>
                  <div class="icon-uncheck" v-else/>
                </div>
                <div class="col">
                  <div class="row p-0">
                    <div>
                      <span class="title">{{price.nickname ? price.nickname : activeChallenge.name}}</span>
                      <div class="free-w-annual" v-if="isAnnualWebOrNativeSubscriber && price.metadata.freeWithAnnual"><Translate directive='challenge.purchase.step1.freeWithAnnual' /></div>
                    </div>
                    <span class="price">{{challengePrice(price)}}</span>
                  </div>
                </div>
              </div>
          </b-col>
        </b-row>
      </template>
      <BaseButton :disabled="loading" class="unlock-challenge mb-5" @click.prevent="handleClick"><b-spinner v-show="loading" small type="grow"></b-spinner><span v-show="!loading">{{ this.$_polyglot.t(buttonText)}}</span></BaseButton>
    </div>
  </div>
</template>

<style lang='scss' scoped>
@import '@design';
.modal-content {
  width: 100% !important;
}
.container {
  background-color: unset !important;
}
.challenge-image {
    background-color: white;
    z-index: 1;
    border-top-right-radius: 24px;
    border-top-left-radius: 24px;
    height: 0;
    overflow: hidden;
    padding-top: calc(127 / 236 * 100%);
    position: relative;
    .challenge-image-inside {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }
}
.challenge-details {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 32px;
  position: relative;
  padding-left: 8px;
  padding-right: 8px;

  .challenge-header {
    font-family: $roboto-font-family;
    font-style: normal;
    font-weight: normal;
    font-size: 40px;
    line-height: 47px;
    text-align: center;
    color: #000000;
    margin-bottom: 0;
    @media screen and (max-width: 400px) {
      font-size: 25px;
    }
  }
  .description {
    font-family: $base-font-family;
    font-style: normal;
    font-weight: normal;
    font-size: 24px;
    line-height: 33px;
    text-align: center;
    color: #000000;
    opacity: 0.5;
     @media screen and (max-width: 400px) {
      font-size: 18px;
    }
  }

  .row.challenge-card {
    border: 1px solid rgba(0, 0, 0, 0.125);
    box-shadow: 0px 2px 4px rgba(214, 214, 214, 0.08);
    border-radius: 8px;
    padding: 16px;
    margin: 0 auto;
    width: 80%;
    &.selected {
      border: 3px solid var(--BGColor);
      background: rgba(195, 153, 242, 0.09);
    }
    &:not(.selected) {
      transition: transform 300ms;
      transition-timing-function: ease-out;
      &:hover {
        transform: scale(1.01);
      }
      cursor: pointer;
    }
    @media screen and (max-width: 400px) {
      padding: 0 8px 8px 8px;
      width: 90%;
    }

    .challenge-price {
      display: flex;
      flex-direction: column;
      padding: 0;

      .row {
        display: flex;
        align-items: center;
        padding: 1rem;
        font-family: $base-font-family;
        font-style: normal;

        .title {
          font-weight: bold;
          font-size: 19px;
          line-height: 26px;
          color: #000000;
          margin-right: auto;
          margin-left: 0px;
           @media screen and (max-width: 400px) {
            font-size: 14px;
          }
        }

        .price {
          font-weight: 600;
          font-size: 18px;
          line-height: 25px;
          display: block;
          margin-right: 0;
          margin-left: auto;
          color: rgba(0, 0, 0, 0.85);
          white-space: nowrap;
           @media screen and (max-width: 400px) {
            font-size: 15px;
          }
        }

        .check-container {
          margin-right: 12px;
          .icon-check {
            height: 2rem;
            width: 2rem;
            @media screen and (max-width: 400px) {
              height: 1.5rem;
              width: 1.5rem;
            }
            path {
              &:first-child {
                fill: var(--BGColor);
                fill-opacity: 1;
              }
            }
          }
          .icon-uncheck {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            border: 1px black solid;
            cursor: pointer;
          }
        }
      }
      .free-w-annual {
        color: var(--BGColor);
        cursor: unset;
      }
    }

  }
  button.button.unlock-challenge {
    background: #2F2E2E;
    border-radius: 60px;
    margin: 0 auto;
    font-family: $roboto-font-family;
    font-style: normal;
    font-weight: bold;
    font-size: 20px;
    line-height: 23px;
    text-align: center;
    padding: 17px 40px;
    margin-top: 2rem;
    color: #FFFFFF;
  }
}

</style>
